import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Container, Typography, CircularProgress, Box, Button, CssBaseline
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import TopBar from "./components/TopBar";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import html2canvas from "html2canvas";
import { API_BASE } from "./config";

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const uid = searchParams.get("uid"); // ✅ grab vending machine UID from query param
  const navigate = useNavigate();

  const [qrCode, setQrCode] = useState("");
  const [expiry, setExpiry] = useState(null);
  const [serial, setSerial] = useState("");
  const [location, setLocation] = useState("");
  const [countdown, setCountdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState(() => localStorage.getItem("themeMode") || "light");

  const pageRef = useRef(null);
  const theme = useMemo(() => createTheme({ palette: { mode } }), [mode]);

  const toggleTheme = () => {
    const next = mode === "light" ? "dark" : "light";
    localStorage.setItem("themeMode", next);
    setMode(next);
  };

  const fetchQrCode = async (attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      setError(true);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE}/zts/app/v1.0/check/payment_status/${sessionId}/`);

      if (response.data.success && response.data.data) {
        setQrCode(response.data.data);
        setExpiry(response.data.expiry);
        setSerial(response.data.serial || "HPHT");
        setLocation(response.data.location || "Success Page");
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error("Polling error:", err);
    }

    setTimeout(() => fetchQrCode(attempts + 1), 3000);
  };

  useEffect(() => {
    if (!sessionId) {
      setError(true);
      setLoading(false);
      return;
    }
    fetchQrCode();

    // ✅ Intercept back button to go back to vending machine page
    const handlePopState = () => {
      navigate(`/?uid=${uid}`, { replace: true });
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [sessionId, uid, navigate]);

  useEffect(() => {
    if (!expiry) return;
    const interval = setInterval(() => {
      const secondsLeft = Math.max(0, Math.floor((new Date(expiry).getTime() - Date.now()) / 1000));
      setCountdown(secondsLeft);
    }, 1000);
    return () => clearInterval(interval);
  }, [expiry]);

  const handleRetry = () => {
    setQrCode("");
    setError(false);
    setLoading(true);
    fetchQrCode();
  };

  const handleDownloadPage = async () => {
    if (!pageRef.current) return;
    const canvas = await html2canvas(pageRef.current);
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `SuccessPage-${sessionId}.png`;
    link.click();
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TopBar
        serial={loading ? "Checking Payment Status..." : error ? "PAYMENT NOT RECEIVED" : serial}
        location={loading || error ? "" : location}
        mode={mode}
        toggleTheme={toggleTheme}
      />

      <Container maxWidth="sm" sx={{ textAlign: "center", mt: 5 }} ref={pageRef}>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Session ID: <strong>{sessionId}</strong>
        </Typography>

        <Typography variant="body1" sx={{ mt: 2 }}>
          {loading
            ? "Please wait while we confirm your transaction..."
            : error
            ? ""
            : "Scan the QR code below to collect your item."}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 3 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              href={`https://api.whatsapp.com/send?phone=60164709459&text=${sessionId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              CONTACT CUSTOMER SERVICE
            </Button>
          </Box>
        ) : (
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: "inline-block",
                animation: countdown && countdown > 0 ? "pulse 1.5s infinite" : "none",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.1)" },
                  "100%": { transform: "scale(1)" }
                }
              }}
            >
              <QRCodeCanvas
                value={qrCode}
                size={window.innerWidth < 480 ? 240 : 200}
                level="H"
                includeMargin={true}
              />
            </Box>

            {expiry && (
              <>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  QR Code expires at: <strong>{new Date(expiry).toLocaleString()}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Countdown:{" "}
                  <strong>{countdown === 0 ? "QR EXPIRED" : `${countdown} seconds`}</strong>
                </Typography>
              </>
            )}

            <Button variant="outlined" sx={{ mt: 2 }} onClick={handleDownloadPage}>
              Save This Page as Image
            </Button>

            <Typography variant="body2" sx={{ mt: 2 }}>
              Have feedback?{" "}
              <a
                href={`https://api.whatsapp.com/send?phone=60164709459&text=${sessionId}&from=${uid}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Chat on WhatsApp
              </a>
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => navigate(`/?uid=${uid}`, { replace: true })}
              >
                Purchase Another Item
              </Button>
            </Box>
          </Box>
        )}
      </Container>
    </ThemeProvider>
  );
}


// import React, { useEffect, useRef, useState, useMemo } from "react";
// import {
//   Container, Typography, CircularProgress, Box, Button, CssBaseline
// } from "@mui/material";
// import { useSearchParams } from "react-router-dom";
// import { QRCodeCanvas } from "qrcode.react";
// import axios from "axios";
// import TopBar from "./components/TopBar";
// import { createTheme, ThemeProvider } from "@mui/material/styles";
// import html2canvas from "html2canvas";
// import { API_BASE } from "./config";

// export default function SuccessPage() {
//   const [searchParams] = useSearchParams();
//   const sessionId = searchParams.get("session_id");
//   const fromUid = searchParams.get("from");

//   const [qrCode, setQrCode] = useState("");
//   const [expiry, setExpiry] = useState(null);
//   const [serial, setSerial] = useState("");
//   const [location, setLocation] = useState("");
//   const [countdown, setCountdown] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(false);
//   const [mode, setMode] = useState(() => localStorage.getItem("themeMode") || "light");

//   const pageRef = useRef(null);

//   const theme = useMemo(() => createTheme({ palette: { mode } }), [mode]);
//   const toggleTheme = () => {
//     const next = mode === "light" ? "dark" : "light";
//     localStorage.setItem("themeMode", next);
//     setMode(next);
//   };

//   const fetchQrCode = async (attempts = 0) => {
//     const maxAttempts = 10;
//     if (attempts >= maxAttempts) {
//       setError(true);
//       setLoading(false);
//       return;
//     }

//     try {
//       const response = await axios.get(
//         `${API_BASE}/zts/app/v1.0/check/payment_status/${sessionId}/`
//       );

//       if (response.data.success && response.data.data) {
//         setQrCode(response.data.data);
//         setExpiry(response.data.expiry);
//         setSerial(response.data.serial || "ZTS");
//         setLocation(response.data.location || "Success Page");
//         setLoading(false);

//         // ✅ Replace current history entry to prevent back navigation
//         window.history.replaceState(null, "", window.location.pathname + window.location.search);
//         return;
//       }
//     } catch (err) {
//       console.error("Polling error:", err);
//     }

//     setTimeout(() => fetchQrCode(attempts + 1), 3000);
//   };

//   useEffect(() => {
//     if (!sessionId) {
//       setError(true);
//       setLoading(false);
//       return;
//     }
//     fetchQrCode();
//   }, [sessionId]);

//   useEffect(() => {
//     if (!expiry) return;
//     const interval = setInterval(() => {
//       const secondsLeft = Math.max(0, Math.floor((new Date(expiry).getTime() - Date.now()) / 1000));
//       setCountdown(secondsLeft);
//     }, 1000);
//     return () => clearInterval(interval);
//   }, [expiry]);

//   const handleRetry = () => {
//     setQrCode("");
//     setError(false);
//     setLoading(true);
//     fetchQrCode();
//   };

//   const handleDownloadPage = async () => {
//     if (!pageRef.current) return;
//     const canvas = await html2canvas(pageRef.current);
//     const link = document.createElement("a");
//     link.href = canvas.toDataURL("image/png");
//     link.download = `SuccessPage-${sessionId}.png`;
//     link.click();
//   };

//   return (
//     <ThemeProvider theme={theme}>
//       <CssBaseline />
//       <TopBar
//         serial={
//           loading
//             ? "Checking Payment Status..."
//             : error
//             ? "PAYMENT NOT RECEIVED"
//             : serial
//         }
//         location={loading || error ? "" : location}
//         mode={mode}
//         toggleTheme={toggleTheme}
//       />

//       <Container maxWidth="sm" sx={{ textAlign: "center", mt: 5 }} ref={pageRef}>
//         <Typography variant="subtitle2" sx={{ mt: 1 }}>
//           Session ID: <strong>{sessionId}</strong>
//         </Typography>

//         <Typography variant="body1" sx={{ mt: 2 }}>
//           {loading
//             ? "Please wait while we confirm your transaction..."
//             : error
//             ? ""
//             : "Scan the QR code below to collect your item."}
//         </Typography>

//         {loading ? (
//           <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 3 }}>
//             <CircularProgress />
//             <Typography sx={{ mt: 2 }}>Loading...</Typography>
//           </Box>
//         ) : error ? (
//           <Box sx={{ mt: 4 }}>
//             <Button
//               variant="contained"
//               color="primary"
//               size="large"
//               href={`https://api.whatsapp.com/send?phone=60164709459&text=${sessionId}`}
//               target="_blank"
//               rel="noopener noreferrer"
//             >
//               CONTACT CUSTOMER SERVICE
//             </Button>
//             <Box sx={{ mt: 2 }}>
//               <Button variant="outlined" onClick={handleRetry}>
//                 Retry
//               </Button>
//             </Box>
//           </Box>
//         ) : (
//           <Box sx={{ mt: 3 }}>
//             <Box
//               sx={{
//                 display: "inline-block",
//                 animation: countdown && countdown > 0 ? "pulse 1.5s infinite" : "none",
//                 "@keyframes pulse": {
//                   "0%": { transform: "scale(1)" },
//                   "50%": { transform: "scale(1.1)" },
//                   "100%": { transform: "scale(1)" }
//                 }
//               }}
//             >
//               <QRCodeCanvas
//                 value={qrCode}
//                 size={window.innerWidth < 480 ? 240 : 200}
//                 level="H"
//                 includeMargin={true}
//               />
//             </Box>

//             {expiry && (
//               <>
//                 <Typography variant="body2" sx={{ mt: 2 }}>
//                   QR Code expires at: <strong>{new Date(expiry).toLocaleString()}</strong>
//                 </Typography>
//                 <Typography variant="body2" sx={{ mt: 1 }}>
//                   Countdown:{" "}
//                   <strong>{countdown === 0 ? "QR EXPIRED" : `${countdown} seconds`}</strong>
//                 </Typography>
//               </>
//             )}

//             <Button variant="outlined" sx={{ mt: 2 }} onClick={handleDownloadPage}>
//               Save This Page as Image
//             </Button>

//             {fromUid && (
//               <Button
//                 variant="contained"
//                 color="secondary"
//                 sx={{ mt: 2 }}
//                 href={`/?uid=${fromUid}`}
//               >
//                 Purchase Another Item
//               </Button>
//             )}

//             <Typography variant="body2" sx={{ mt: 2 }}>
//               Have feedback?{" "}
//               <a
//                 href={`https://api.whatsapp.com/send?phone=60164709459&text=${sessionId}`}
//                 target="_blank"
//                 rel="noopener noreferrer"
//               >
//                 Chat on WhatsApp
//               </a>
//             </Typography>
//           </Box>
//         )}
//       </Container>
//     </ThemeProvider>
//   );
// }


// import React, { useEffect, useRef, useState, useMemo } from "react";
// import {
//   Container, Typography, CircularProgress, Box, Button, CssBaseline
// } from "@mui/material";
// import { useSearchParams } from "react-router-dom";
// import { QRCodeCanvas } from "qrcode.react";
// import axios from "axios";
// import TopBar from "./components/TopBar";
// import { createTheme, ThemeProvider } from "@mui/material/styles";
// import html2canvas from "html2canvas";
// import { API_BASE } from "./config";

// export default function SuccessPage() {
//   const [searchParams] = useSearchParams();
//   const sessionId = searchParams.get("session_id");

//   const [qrCode, setQrCode] = useState("");
//   const [expiry, setExpiry] = useState(null);
//   const [serial, setSerial] = useState("");
//   const [location, setLocation] = useState("");
//   const [countdown, setCountdown] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(false);
//   const [mode, setMode] = useState(() => localStorage.getItem("themeMode") || "light");

//   const pageRef = useRef(null);

//   const theme = useMemo(() => createTheme({ palette: { mode } }), [mode]);
//   const toggleTheme = () => {
//     const next = mode === "light" ? "dark" : "light";
//     localStorage.setItem("themeMode", next);
//     setMode(next);
//   };

//   const fetchQrCode = async (attempts = 0) => {
//     const maxAttempts = 10;
//     if (attempts >= maxAttempts) {
//       setError(true);
//       setLoading(false);
//       return;
//     }

//     try {
//       const response = await axios.get(
//         `${API_BASE}/zts/app/v1.0/check/payment_status/${sessionId}/`

//       );

//       if (response.data.success && response.data.data) {
//         setQrCode(response.data.data);
//         setExpiry(response.data.expiry);
//         setSerial(response.data.serial || "ZTS");
//         setLocation(response.data.location || "Success Page");
//         setLoading(false);
//         return;
//       }
//     } catch (err) {
//       console.error("Polling error:", err);
//     }

//     setTimeout(() => fetchQrCode(attempts + 1), 3000);
//   };

//   useEffect(() => {
//     if (!sessionId) {
//       setError(true);
//       setLoading(false);
//       return;
//     }
//     fetchQrCode();
//   }, [sessionId]);

//   useEffect(() => {
//     if (!expiry) return;
//     const interval = setInterval(() => {
//       const secondsLeft = Math.max(0, Math.floor((new Date(expiry).getTime() - Date.now()) / 1000));
//       setCountdown(secondsLeft);
//     }, 1000);
//     return () => clearInterval(interval);
//   }, [expiry]);

//   const handleRetry = () => {
//     setQrCode("");
//     setError(false);
//     setLoading(true);
//     fetchQrCode();
//   };

//   const handleDownloadPage = async () => {
//     if (!pageRef.current) return;
//     const canvas = await html2canvas(pageRef.current);
//     const link = document.createElement("a");
//     link.href = canvas.toDataURL("image/png");
//     link.download = `SuccessPage-${sessionId}.png`;
//     link.click();
//   };

//   return (
//     <ThemeProvider theme={theme}>
//       <CssBaseline />
//       <TopBar
//         serial={
//           loading
//             ? "Checking Payment Status..."
//             : error
//             ? "PAYMENT NOT RECEIVED"
//             : serial
//         }
//         location={loading || error ? "" : location}
//         mode={mode}
//         toggleTheme={toggleTheme}
//       />

//       <Container maxWidth="sm" sx={{ textAlign: "center", mt: 5 }} ref={pageRef}>
//         <Typography variant="subtitle2" sx={{ mt: 1 }}>
//           Session ID: <strong>{sessionId}</strong>
//         </Typography>

//         <Typography variant="body1" sx={{ mt: 2 }}>
//           {loading
//             ? "Please wait while we confirm your transaction..."
//             : error
//             ? ""
//             : "Scan the QR code below to collect your item."}
//         </Typography>

//         {loading ? (
//           <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 3 }}>
//             <CircularProgress />
//             <Typography sx={{ mt: 2 }}>Loading...</Typography>
//           </Box>
//         ) : error ? (
//           <Box sx={{ mt: 4 }}>
//             <Button
//               variant="contained"
//               color="primary"
//               size="large"
//               href={`https://api.whatsapp.com/send?phone=60164709459&text=${sessionId}`}
//               target="_blank"
//               rel="noopener noreferrer"
//             >
//               CONTACT CUSTOMER SERVICE
//             </Button>
//           </Box>
//         ) : (
//           <Box sx={{ mt: 3 }}>
//             <Box
//               sx={{
//                 display: "inline-block",
//                 animation: countdown && countdown > 0 ? "pulse 1.5s infinite" : "none",
//                 "@keyframes pulse": {
//                   "0%": { transform: "scale(1)" },
//                   "50%": { transform: "scale(1.1)" },
//                   "100%": { transform: "scale(1)" }
//                 }
//               }}
//             >
//               <QRCodeCanvas
//                 value={qrCode}
//                 size={window.innerWidth < 480 ? 240 : 200}
//                 level="H"
//                 includeMargin={true}
//               />
//             </Box>

//             {expiry && (
//               <>
//                 <Typography variant="body2" sx={{ mt: 2 }}>
//                   QR Code expires at: <strong>{new Date(expiry).toLocaleString()}</strong>
//                 </Typography>
//                 <Typography variant="body2" sx={{ mt: 1 }}>
//                   Countdown:{" "}
//                   <strong>{countdown === 0 ? "QR EXPIRED" : `${countdown} seconds`}</strong>
//                 </Typography>
//               </>
//             )}

//             <Button variant="outlined" sx={{ mt: 2 }} onClick={handleDownloadPage}>
//               Save This Page as Image
//             </Button>

//             <Typography variant="body2" sx={{ mt: 2 }}>
//               Have feedback?{" "}
//               <a
//                 href={`https://api.whatsapp.com/send?phone=60164709459&text=${sessionId}`}
//                 target="_blank"
//                 rel="noopener noreferrer"
//               >
//                 Chat on WhatsApp
//               </a>
//             </Typography>
//           </Box>
//         )}
//       </Container>
//     </ThemeProvider>
//   );
// }

